import React from 'react';
import { Box, Paper, Table, TableBody, TableContainer } from '@mui/material';
import { EmployeeSpreadsheetHeader } from './EmployeeSpreadsheetHeader';
import { EmployeeDataRow } from './EmployeeDataRow';
import type { Employee } from '../../types/employee';
import type { EditingCell, EmployeeSpreadsheetCellField } from './types';
import { renderSeveranceMessage } from './hooks';

interface EmployeeSpreadsheetProps {
  employees: Employee[];
  employeeSeveranceInfo: Record<string, { isEligible: boolean; daysUntilEligibility: number | null; severancePay: number | null } | null>;
  selectedIds: string[];
  onSelectAll: (checked: boolean) => void;
  onSelectEmployee: (id: string) => void;
  editingCell: EditingCell | null;
  onStartEdit: (employeeId: string, field: EmployeeSpreadsheetCellField) => void;
  onSave: (employeeId: string, field: EmployeeSpreadsheetCellField, value: string | number | boolean) => void;
  onCancelEdit: () => void;
  onMenuOpen: (event: React.MouseEvent<HTMLElement>, employee: Employee) => void;
}

export const EmployeeSpreadsheet: React.FC<EmployeeSpreadsheetProps> = ({
  employees,
  employeeSeveranceInfo,
  selectedIds,
  onSelectAll,
  onSelectEmployee,
  editingCell,
  onStartEdit,
  onSave,
  onCancelEdit,
  onMenuOpen,
}) => {
  const allSelected = employees.length > 0 && selectedIds.length === employees.length;
  const someSelected = selectedIds.length > 0;

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      <TableContainer sx={{ maxHeight: 'calc(100vh - 320px)' }}>
        <Table stickyHeader size="small" sx={{ tableLayout: 'fixed' }}>
          <EmployeeSpreadsheetHeader
            allSelected={allSelected}
            someSelected={someSelected}
            onSelectAll={onSelectAll}
          />
          <TableBody>
            {employees.map((employee) => (
              <EmployeeDataRow
                key={employee.id}
                employee={employee}
                severanceMessage={renderSeveranceMessage(employeeSeveranceInfo[employee.id] || null)}
                isSelected={selectedIds.includes(employee.id)}
                onSelect={() => onSelectEmployee(employee.id)}
                editingCell={editingCell}
                onStartEdit={(field) => onStartEdit(employee.id, field)}
                onSave={(field, value) => onSave(employee.id, field, value)}
                onCancelEdit={onCancelEdit}
                onMenuOpen={(e) => onMenuOpen(e, employee)}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};
